<template>
    <div class="flex flex-col lg:flex-row justify-between items-center">
        <h2 class="text-xl leading-9 font-headline font-medium text-bhav-blue-600">Lots &amp; Parcels</h2>
        <nav class="flex items-end gap-2">
            <ViewToggle v-model="tab" :views="viewOptions" />
        </nav>
    </div>

    <section v-if="tab === 'list'">
        <h3 class="sr-only">List View</h3>

        <div class="flex w-full flex-col flex-wrap md:flex-row md:justify-start gap-4 mt-8">
            <div class="mb-4 flex-initial min-w-80">
                <label for="search" class="block text-sm font-medium leading-5 pb-1.5 text-blue-500">Keywords</label>
                <SearchInput v-model="query.search" class="min-w-100" color="gray" placeholder="Search..." />
            </div>
            <div class="flex-initial">
                <SelectField v-model="query.matches" label="Parcel Matches" empty="All Parcel Matches" :options="[{ label: 'Matched', value: 'matched' }, { label: 'Unmatched', value: 'unmatched' }]"/>
            </div>
            <div class="flex-initial">
                <SelectMultipleField v-model="query.status" label="Status"  empty="All Statuses" all="All Statuses" :options="[...new Set(lots.map(lot => lot.kova.Status))].map(status => ( { label: status, value: status } ))"/>
            </div>
            <div v-if="hasFilters" class="mb-4 flex-initial sm:mt-5">
                <FormButton @click="reset" color="blue">Clear Filters</FormButton>
            </div>
            <div class="mb-4 flex-initial sm:mt-6 ml-auto">
                <button @click="showColumns = !showColumns" class="w-9 h-9 items-center justify-center bg-transparent hover:bg-bhav-cool-gray-50 text-bhav-blue-200 hover:text-bhav-blue-500 rounded-lg text-lg">
                    <p class="relative">
                        <span class="fak fa-col-options leading-none"></span>
                        <span class="sr-only">Open Table Column Preferences Drawer</span>
                    </p>
                </button>
            </div>
        </div>
        <PaginationTable empty-message="There are currently no lot items." :data="filter" :columns="columns" :view="`${$route.name}-${$route.params.tab}`" :legacy="false" :show-columns="showColumns" @closeDrawer="showColumns = false">
            <template #col-lot_id="{row}">
                {{ row.id ?? 'ummm....' }}
            </template>
            <template #col-name="{row}">
                {{ row.kova?.Name }}
            </template>
            <template #col-collection="{row}">
                {{ collections.find(collection => collection.id === row.collection_id)?.meta.info.name }}
            </template>
            <template #col-address="{row}">
                {{ row.kova?.StreetAddress }}, {{ row.kova?.City }} {{ row.kova?.ZipCode }}
            </template>
            <template #col-parcel_id="{row}">
                {{ row.parcel_id || '-' }}
            </template>
        </PaginationTable>
    </section>
    <section v-else>
        <nav class="flex justify-end mt-4">
            <Dropdown 
                label="Actions"
                :options="[
                    { 
                        label: 'Download SVG', 
                        click: download, 
                        icon: downloading ? 'fak fa-history' : 'fak fa-download' 
                    }
                ]"/>
        </nav>
        <MapView v-if="lots.length && parcels.length" :sources="sources" :layers="layers" @selected="setSelected" class="mt-4 md:h-[50vh]" :clickable="true">
            <template #legend>
                <dl>
                    <dt class="text-oakwood-gray-500 text-sm font-bold mt-4 sr-only">Key</dt>
                    <dd class="text-bhav-blue-500 text-sm flex items-center gap-1">
                        <span title="Green" class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-oakwood-green-700"></span> Available Home
                    </dd>
                    <dd class="text-bhav-blue-500 text-sm flex items-center gap-1">
                        <span title="Orange" class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-oakwood-orange-500"></span> Model Home
                    </dd>
                    <dd class="text-bhav-blue-500 text-sm flex items-center gap-1">
                        <span title="Blue" class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-bhav-blue-500"></span> New Lot
                    </dd>
                    <dd class="text-bhav-blue-500 text-sm flex items-center gap-1">
                        <span title="Blue" class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-bhav-blue-700"></span> Available Lot
                    </dd>
                    <dd class="text-bhav-blue-500 text-sm flex items-center gap-1">
                        <span title="Light Amber" class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-gold-200"></span> Sold
                    </dd>
                    <dd class="text-bhav-blue-500 text-sm flex items-center gap-1">
                        <span title="Amber" class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-gold-400"></span> Sold Contingent
                    </dd>
                    <dd class="text-bhav-blue-500 text-sm flex items-center gap-1">
                        <span title="Red" class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-oakwood-red-500"></span> Closed
                    </dd>
                    <dd class="text-bhav-blue-500 text-sm flex items-center gap-1">
                        <span title="Dark Gray" class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-oakwood-gray-800"></span> Void
                    </dd>
                    <dd class="text-bhav-blue-500 text-sm flex items-center gap-1">
                        <span title="Light Gray" class="inline-block w-4 h-4 border border-oakwood-gray-500 bg-oakwood-gray-200"></span> Unassigned
                    </dd>
                </dl>
            </template>
        </MapView>
        <ModalDialog v-if="selected" :open="selected !== false" @close="close()" class="min-w-[36rem]" verticalScroll="overlap">
            <template #header>
                <h2 class="text-lg leading-6 font-medium text-bhav-blue-500" id="modal-headline">
                    Edit Parcel Lot Assignment
                </h2>
            </template>
            <template #body>
                <section class="bg-oakwood-gray-50 p-4 rounded mb-8 grid grid-cols-1 gap-4">
                    <h3 class="sr-only">Parcel Details</h3>
                    <dl class="grid grid-cols-4 gap-1 text-oakwood-gray-500">
                        <dt class="font-bold">Area</dt>
                        <dd class="col-span-3 text-bhav-blue-500">{{ selected?.properties?.area ? `${formatToNumber(Math.round(selected.properties?.area))} sqft` : '-' }}</dd>
                        <dt class="font-bold">Filing</dt>
                        <dd class="col-span-3 text-bhav-blue-500">{{ selected?.properties?.filing ?? '-' }}</dd>
                        <dt class="font-bold">Block</dt>
                        <dd class="col-span-3 text-bhav-blue-500">{{ selected?.properties?.block ?? '-' }}</dd>
                        <dt class="font-bold">Lot</dt>
                        <dd class="col-span-3 text-bhav-blue-500">{{ selected?.properties?.lot ?? '-' }}</dd>
                        <dt class="font-bold">Available Home</dt>
                        <dd class="col-span-3 text-bhav-blue-500">
                            <router-link v-if="availableHome?.id" :to="{name: 'available-homes-show', params: { id: availableHome.id }}">{{ availableHome.slug }}</router-link>
                            <template v-else>-</template>
                        </dd>
                        <dt class="font-bold">Center Point</dt>
                        <dd class="col-span-1 text-bhav-blue-500">{{ selected?.properties?.lng && `Longitude ${Intl.NumberFormat('en-US', { maximumFractionDigits: 5 }).format(selected.properties?.lng)}º`  }}</dd>
                        <dd class="col-span-2 text-bhav-blue-500">{{ selected?.properties?.lat && `Latitude ${Intl.NumberFormat('en-US', { maximumFractionDigits: 5 }).format(selected.properties?.lat)}º`  }}</dd>
                        <template v-if="lot.id">
                            <dt class="font-bold">Kova Lot ID</dt>
                            <dl class="col-span-3 text-bhav-blue-500">{{ currentLot?.kova?.LotRID }}</dl>
                            <dt class="font-bold">Type</dt>
                            <dl class="col-span-3 text-bhav-blue-500">{{ currentLot?.kova?.Type }}</dl>
                            <dt class="font-bold">Status</dt>
                            <dl class="col-span-3 text-bhav-blue-500">{{ currentLot?.kova?.Status }}</dl>
                        </template>
                    </dl>
                </section>
                <p>
                    <SelectField :focus="true" class="w-full" :show-label="false" @keydown="submit" v-model="lot.id" empty="Select a lot..." :options="availableLots(lot.id).map(availableLot => ( { label: `${availableLot.kova.StreetAddress }, ${ availableLot.kova.City } ${ availableLot.kova.ZipCode }`, value: availableLot.id } ))" />
                </p>
            </template>
            <template #buttons>
                <FormButton type="button" @click="close()" color="gray">
                    Cancel
                </FormButton>
                <FormButton @click="save" color="blue">
                    Save
                </FormButton>
            </template>
        </ModalDialog>
    </section>
</template>

<script>
import axios from "axios";
import PaginationTable from '@/components/PaginationTable.vue';
import MapView from '@/components/MapView.vue';
import colors from '@/helpers/colors';
import ModalDialog from "../ModalDialog.vue";
import { SelectField, FormButton, SelectMultipleField } from "../form";
import { formatToNumber } from "@/helpers/numberHelpers";
import SearchInput from "../form/SearchInput.vue";
import ViewToggle from "../ViewToggle.vue";
import Dropdown from "../form/Dropdown.vue";
const query = {
    search: null,
    matches: null,
    status: []
};

export default {
    setup() {
        return {
            formatToNumber,
        }
    },
    data() {
        return {
            tab: "list",
            lots: [],
            parcels: [],
            selected: false,
            lot: {
                id: null,
                parcel_id: null
            },
            query: {...query},
            downloading: false,
            showColumns: false,
            viewOptions: [
                { value: 'list', icon: 'fak fa-list-view', label: 'Switch to List View' },
                { value: 'map', icon: 'fak fa-map', label: 'Switch to Map View' }
            ]
        }
    },
    methods: {
        async index() {
            await axios.get(`${this.context}/${this.id}/lots`)
                .then((res) => { this.lots = res.data })
                .catch((err) => { console.error(err) });
        },
        async indexParcels() {
            await axios.get(`${this.context}/${this.id}/parcels`)
                .then((res) => { this.parcels = res.data })
                .catch((err) => { console.error(err) });
        },
        async download() {
            this.downloading = true;
            await axios.get(`${this.context}/${this.id}/parcels/svg`, {headers: {'Accept' : 'image/svg+xml'}, responseType: 'blob'})
                .then((res) => {
                    const file = res.headers['content-disposition'].split('; ')[1].split('=')[1] ?? 'file.svg';
                    // create file link in browser's memory
                    const href = URL.createObjectURL(res.data);

                    // create "a" HTML element with href to file & click
                    const link = document.createElement('a');
                    link.href = href;
                    link.setAttribute('download', file); //or any other extension
                    document.body.appendChild(link);
                    link.click();

                    // clean up "a" element & remove ObjectURL
                    document.body.removeChild(link);
                    URL.revokeObjectURL(href);
                })
                .catch((err) => { console.error(err) })
                .finally(() => {
                    this.downloading = false;
                })
        },
        async save() {
            if(!this.selected) return;
            await axios.patch(`parcels/${this.lot.parcel_id}`, {lot_id: this.lot.id})
                .then(() => { 
                    this.index();
                    this.selected = false;
                })
                .catch((err) => { console.error(err); });
        },
        setSelected(selected) {
            this.selected = selected;
            this.lot = {
                id: selected?.properties?.lot_id || null,
                parcel_id: selected?.properties?.id || null
            }
        },
        close() {
            this.selected = false;
        },
        availableLots(current) {
            return this.lots.filter(lot => {
                return lot.parcel_id === null || lot.id === current;
            }).sort((a,b) => a.kova.StreetAddress.toLowerCase() > b.kova.StreetAddress.toLowerCase() ? 1 : -1 );
        },
        submit(e) {
            if(e.key === 'Enter') {
                e.preventDefault();
                this.save();
                return false;
            }
            return true;
        },
        reset() {
            this.query = {...query};
        }
    },
    computed: {
        columns() {
            return [
              {
                name: 'lot_id',
                label: 'Lot ID',
                sort: (row) => row.id || "",
                default: true
              },
              {
                name: 'name',
                label: 'Name',
                sort: (row) => row.kova?.Name || "",
                default: true
              },
              {
                name: 'collection',
                label: 'Collection',
                sort: (row) => this.collections.find(collection => collection.id === row.collection_id)?.meta.info.name || "",
                default: true
              },
              {
                name: 'address',
                label: 'Address',
                sort: (row) => row.kova?.StreetAddress + ', ' + row.kova?.City + ' ' + row.kova?.ZipCode,
                default: true
              },
              {
                name: 'parcel_id',
                label: 'Parcel ID',
                sort: (row) => row.parcel_id || "-",
                default: true
              },
            ]
        },
        features() {
            return this.parcels
                .map(parcel => {
                    const {id, data, lot} = parcel;
                    
                    return {
                        ...data,
                        id,
                        properties: {
                            lat: parcel?.centroid[1],
                            lng: parcel?.centroid[0],
                            ...data.properties,
                            lot: lot.lot_number ?? null,
                            block: lot.block_number ?? null,
                            id,
                            sales_order: lot.sales_order,
                            clickable: lot?.sales_order?.status === 'active' && ['New', 'Available'].includes(lot.kova?.Status),
                            lot_id: lot.id ?? false,
                            status: lot?.kova?.Status ?? null,
                            type: lot?.kova?.Type ?? null
                        }
                    }
                });
        },
        points() {
            return this.parcels
                .map(parcel => {
                    const {id, lot} = parcel;
                        
                    return {
                        type: "Feature",
                        geometry: {
                            type: "Point",
                            coordinates: parcel.centroid
                        },
                        properties: {
                            id,
                            lat: parcel?.centroid[1],
                            lng: parcel?.centroid[0],
                            sales_order: lot?.sales_order,
                            clickable: lot?.sales_order?.status === 'active' && ['New', 'Available'].includes(lot?.kova?.Status),
                            name: lot?.kova?.LotID,
                            lot_rid: lot.lot_rid,
                            lot_id: lot.id,
                            status: lot?.kova?.Status ?? null,
                            street: lot?.kova?.StreetAddress,
                            city: lot?.kova?.City,
                            state: lot?.kova?.State,
                            zip: lot?.kova?.Zip,
                            type: lot?.kova?.Type ?? null
                        }
                    }
                });
        },
        sources() {
            return [
                {
                    id: 'lots',
                    name: 'Lot Parcels',
                    features: this.features
                },
                {
                    id: 'lot_points',
                    name: 'Lot Points',
                    features: this.points,
                    cluster: true,
                    clusterMaxZoom: 12,
                    clusterRadius: 100
                }
            ]
        },
        layers() {
            return [
            {
                    id: 'lot-base',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['oakwood-gray'][300],
                            colors['oakwood-gray'][200]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    before: 'lot-new'
                },
                {
                    id: 'lot-new',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['bhav-blue'][400],
                            colors['bhav-blue'][500]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: [ '==', 'status', 'New']
                },
                {
                    id: 'lot-available',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['bhav-blue'][600],
                            colors['bhav-blue'][700]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: [ '==', 'status', 'Available']
                },
                {
                    id: 'lot-market',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['bhav-green'][600],
                            colors['bhav-green'][700]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: [
                        'all',
                        ['coalesce', ['get', 'clickable'], false],
                        ['==', ['get', 'type'], 'Market'],
                        ['in', ['get', 'status'], ['literal', ['New', 'Available']]]
                    ]
                },
                {
                    id: 'lot-model',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['oakwood-orange'][400],
                            colors['oakwood-orange'][500]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: [ '==', 'type', 'Model']
                },
                {
                    id: 'lot-closed',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['oakwood-red'][400],
                            colors['oakwood-red'][500]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: [ '==', 'status', 'Closed']
                },
                {
                    id: 'lot-sold',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['gold'][100],
                            colors['gold'][200]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: [ '==', 'status', 'Sold']
                },
                {
                    id: 'lot-sold-contingent',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['gold'][300],
                            colors['gold'][400]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: [ '==', 'status', 'SoldContingent']
                },
                {
                    id: 'lot-void',
                    type: 'fill',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            colors['oakwood-gray'][800],
                            colors['oakwood-gray'][900]
                        ],
                        'fill-opacity': 1,
                        'fill-outline-color': colors.white,
                    },
                    filter: [ '==', 'status', 'Void']
                },

                {
                    id: 'lot-boundaries',
                    type: 'line',
                    source: 'lots',
                    minzoom: 14,
                    paint: {
                        'line-color': colors.white,
                        'line-width': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            14, .125,
                            24, 4,
                        ]
                    },
                    before: 'lot-labels'
                },
                {
                    id: 'available-homes',
                    type: 'symbol',
                    source: 'lot_points',
                    layout: {
                        'icon-image': 'available-home-white',
                        'icon-size': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            15,
                            .75,
                            24,
                            3
                        ],
                        'icon-allow-overlap': true
                    },
                    minzoom: 16,
                    paint: {
                        'icon-translate': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            17,
                            [0,0],
                            24,
                            [0,30]
                        ],
                    },
                    filter: [ '==', 'type', 'Market']
                },
                {
                    id: 'model-homes',
                    type: 'symbol',
                    source: 'lot_points',
                    layout: {
                        'icon-image': 'model-home-white',
                        'icon-size': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            15,
                            .75,
                            24,
                            3
                        ],
                        'icon-allow-overlap': true
                    },
                    minzoom: 16,
                    paint: {
                        'icon-translate': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            16,
                            [0,0],
                            24,
                            [0,30]
                        ],
                    },
                    filter: [ '==', 'type', 'Model']
                },
                {
                    id: 'lot-labels',
                    type: 'symbol',
                    source: 'lot_points',
                    layout: {
                        'text-field': ['coalesce', ['get', 'street'], 'Unassigned'],
                        'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
                        'text-radial-offset': 0,
                        'text-justify': 'center',
                        'text-size': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            16,
                            12,
                            24,
                            16
                        ],
                        'text-max-width': 25
                    },
                    minzoom: 18,
                    paint: {
                        'text-color': colors['oakwood-gray'][900],
                        'text-halo-color': colors['white'],
                        'text-halo-width': 2,
                        'text-translate': [
                            'interpolate',
                            ['exponential', 1],
                            ['zoom'],
                            16,
                            [0,-12],
                            24,
                            [0,-30]
                        ],
                        'text-emissive-strength': 2
                    },
                },
                {
                    id: 'clusters',
                    type: 'circle',
                    source: 'lot_points',
                    filter: ['has', 'point_count'],
                    paint: {
                        'circle-color': colors['bhav-blue'][700],
                        'circle-radius': [
                            'step',
                            ['get', 'point_count'],
                            10,
                            100,
                            15,
                            300,
                            20,
                            500,
                            25
                        ],
                        'circle-emissive-strength': 1,
                        'circle-stroke-color': colors['white'],
                        'circle-stroke-width': 2
                    }
                },
                {
                    id: 'cluster-count',
                    type: 'symbol',
                    source: 'lot_points',
                    filter: ['has', 'point_count'],
                    layout: {
                        'text-field': ['get', 'point_count_abbreviated'],
                        'text-size': 12
                    },
                    paint: {
                        'text-color': colors['white']
                    }
                }
            ]
        },
        currentLot() {
            return this.lots.find(lot => lot.id === this.lot.id) || false;
        },
        availableHome() {
            const sales_order = JSON.parse(this.selected?.properties?.sales_order);
            if(sales_order) {
                return sales_order
            }
            return null
        },
        filter() {
            let lots = this.lots;
            switch(this.query.matches) {
                case "matched" :
                    lots = lots.filter(lot => lot.parcel_id !== null);
                    break;
                case "unmatched" :
                    lots = lots.filter(lot => lot.parcel_id === null);
                    break;
            }

            if(this.query.status.length > 0) {
                lots = lots.filter(lot => this.query.status.includes(lot.kova.Status));
            }

            if(this.query.search !== null && lots.length) {
                lots = lots.filter(lot => {
                    return JSON.stringify(lot?.kova).toLowerCase().includes(this.query.search.toLowerCase()) ||
                        (lot?.parcel_id && `${lot.parcel_id}`.includes(this.query.search)) ||
                        (lot?.id && `${lot.id}`.includes(this.query.search))
                })
            }

            return lots;
        },
        hasFilters() {
            return JSON.stringify(query) !== JSON.stringify(this.query);
        }
    },
    props: {
        id: {
            type: Number,
            required: true
        },
        collections: {
            type: Array,
            default: () => []
        },
        context: {
            type: String,
            required: true
        }
    },
    watch: {
        tab: {
            handler() {
                this.reset();
            }
        }
    },
    components: {
        PaginationTable,
        MapView,
        ModalDialog,
        SelectField,
        SelectMultipleField,
        FormButton,
        SearchInput,
        ViewToggle,
        Dropdown
    },
    created() {
        this.index();
        this.indexParcels();
    }
}
</script>@/components/form